/**
 * Buy Box: a situação de cada anúncio de catálogo (ganhando, dividindo, perdendo, só listado), o custo do produto,
 * quanto custa ganhar, o que falta (Full, parcelamento, frete) e quem ganha de você. Filtros por situação (inclusive
 * "perdendo por preço"), classificação e atributos do produto; aplicar o buy box em todos do filtro; exportar.
 */
import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, ExternalLink, Eye, Search, Zap } from 'lucide-react';
import { api, brl, dateTime, int, pct } from '../api.ts';
import { ErrorBox, Tag, Thumb } from '../components/ui.tsx';
import { useMe } from '../components/AuthGate.tsx';
import { AttrFilterButton } from '../components/AttrFilter.tsx';
import { ListingCategorySelect } from '../components/ListingCategory.tsx';
import { can } from '@berytus/contracts';
import { downloadCsv } from '../csv.ts';
import { Pager, useSearchBox } from './MeliChannel.tsx';

type Status = 'all' | 'winning' | 'sharing_first_place' | 'competing' | 'by_price' | 'listed' | 'changed' | 'no_margin';
interface Row {
  id: string; itemId: string; title: string | null; permalink: string | null; thumbnail: string | null; sku: string | null; cost: number | null; full: boolean;
  status: string; prevStatus: string | null; statusChangedAt: string | null; checkedAt: string | null;
  price: number | null; priceToWin: number | null; winnerPrice: number | null; gapPct: number | null; marginNow: number | null; marginToWin: number | null;
  winnerGapPct: number | null; coverPrice: number | null; marginCover: number | null; priceNotEnough: boolean;
  calc: { cost: number; fees: number; profit: number } | null;
  visitShare: string | null; sharing: number | null; reasons: string[]; myBoosts: string[]; missing: string[]; winnerBoosts: string[];
  winner: { sellerId: number; nickname: string | null; permalink: string | null; itemId: string | null; full: boolean | null } | null;
}
interface Rival { sellerId: number; nickname: string | null; permalink: string | null; products: number; avgGap: number | null; followed: boolean }
interface Page {
  summary: Record<'all' | 'winning' | 'sharing_first_place' | 'competing' | 'by_price' | 'listed' | 'changed', number> & { checked_at: string | null };
  rivals: Rival[]; total: number; page: number; pageSize: number; rows: Row[];
}

const STATUS: Record<string, { label: string; tone: string }> = {
  winning: { label: 'Ganhando', tone: 'ok' },
  sharing_first_place: { label: 'Dividindo o 1º lugar', tone: 'info' },
  competing: { label: 'Perdendo', tone: 'err' },
  listed: { label: 'Só listado', tone: 'warn' },
};
const KPIS: { key: Status; label: string; note: string }[] = [
  { key: 'competing', label: 'Perdendo', note: 'outro vendedor ganha' },
  { key: 'by_price', label: 'Perdendo por preço', note: 'o ML indica um preço menor para ganhar' },
  { key: 'sharing_first_place', label: 'Dividindo o 1º lugar', note: 'visibilidade média' },
  { key: 'listed', label: 'Só listado', note: 'não compete (veja o motivo)' },
  { key: 'winning', label: 'Ganhando', note: 'visibilidade máxima' },
  { key: 'changed', label: 'Mudou de situação', note: 'nos últimos 7 dias' },
];
const COLS: [string, number, string?][] = [['', 56], ['Anúncio', 0], ['Situação', 150], ['Custo', 90, 'num'], ['Meu preço', 96, 'num'], ['Ganhador', 190], ['Margem', 150, 'num'], ['Cobrir', 132], ['O que falta', 180]];
const pctOf = (m: number | null) => (m == null ? '—' : pct(m / 100));
const FILTER_KEYS = ['q', 'seller', 'department', 'category', 'subcategory', 'brand', 'attrs', 'marginMin', 'marginMax', 'marginOf'] as const;
const NUMERIC = new Set(['seller', 'marginMin', 'marginMax']);
interface ApplyAllResult { listings: number; applied: number; skipped: { noProduct: number; noPrice: number; loss: number; outOfRange?: number } }

export function BuyBoxPanel({ params, set, onFollow }: { params: URLSearchParams; set: (p: Record<string, string | null>) => void; onFollow: (sellerId: number) => void }) {
  const [search, setSearch] = useSearchBox(params, set);
  const qc = useQueryClient();
  const status = (params.get('bb') as Status | null) ?? 'all';
  const page = Number(params.get('page') ?? 1);
  const pageSize = Number(params.get('size') ?? 50);
  const q = new URLSearchParams({ status, page: String(page), pageSize: String(pageSize), sort: params.get('sort') ?? 'gap' });
  for (const k of FILTER_KEYS) if (params.get(k)) q.set(k, params.get(k)!);
  const admin = can(useMe().tenant.access, 'buybox', 'write');
  const data = useQuery({
    queryKey: ['meli', 'buybox', q.toString()],
    queryFn: () => api<Page>(`/channels/mercadolivre/buybox?${q}`),
    placeholderData: keepPreviousData,
  });
  const apply = useMutation({
    mutationFn: (b: { id: string; price?: number }) => api<{ price: number }>(`/channels/listings/${b.id}/apply-buybox`, { method: 'POST', json: b.price ? { price: b.price } : {} }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meli', 'buybox'] }),
  });
  // O preço a aplicar acompanha a margem do filtro (margem do preço indicado ou cobrindo o ganhador).
  const mode: 'to_win' | 'cover' = params.get('marginOf') === 'cover' ? 'cover' : 'to_win';
  const setMode = (m: 'to_win' | 'cover') => set({ marginOf: m === 'to_win' ? null : m, page: null });
  const [allowLoss, setAllowLoss] = useState(false);
  const applyAll = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = { status, mode, allowLoss };
      for (const k of FILTER_KEYS) if (params.get(k)) body[k] = NUMERIC.has(k) ? Number(params.get(k)) : params.get(k);
      if (body.marginOf === 'now') delete body.marginOf;
      return api<ApplyAllResult>('/channels/mercadolivre/buybox/apply-all', { method: 'POST', json: body });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meli', 'buybox'] }),
  });
  const [exporting, setExporting] = useState(false);
  const d = data.data;
  const rows = d?.rows ?? [];
  const sellerName = d?.rivals.find((r) => String(r.sellerId) === params.get('seller'))?.nickname;

  const exportAll = async () => {
    setExporting(true);
    try {
      const all = new URLSearchParams(q); all.set('page', '1'); all.set('pageSize', '5000');
      const r = await api<Page>(`/channels/mercadolivre/buybox?${all}`);
      downloadCsv(`buybox_${status}`, ['Anúncio', 'SKU', 'Título', 'Situação', 'Custo', 'Meu preço', 'Preço do ganhador', 'Diferença %', 'Ganhador', 'Margem atual %', 'Preço para cobrir', 'Margem cobrindo %', 'Preço indicado pelo ML', 'O que falta', 'Motivo', 'Mudou em'],
        r.rows.map((x) => [x.itemId, x.sku, x.title, STATUS[x.status]?.label ?? x.status, x.cost, x.price, x.winnerPrice, x.winnerGapPct == null ? null : Math.round(x.winnerGapPct * 1000) / 10,
          x.winner?.nickname ?? x.winner?.sellerId, x.marginNow == null ? null : Math.round(x.marginNow * 10) / 10, x.coverPrice, x.marginCover == null ? null : Math.round(x.marginCover * 10) / 10,
          x.priceToWin, x.missing.join(', '), x.reasons.join(', '), x.statusChangedAt?.slice(0, 10)]));
    } finally {
      setExporting(false);
    }
  };

  if (data.error) return <ErrorBox error={data.error} />;
  return (
    <div className="stack-y">
      {d && d.summary.all === 0 ? (
        <div className="panel empty">
          <b>Nenhum anúncio de catálogo lido ainda</b>
          A situação do buy box é lida junto com a leitura dos anúncios (a cada 6 horas) e quando o Mercado Livre avisa uma mudança.
        </div>
      ) : (
        <>
          {d && (
            <div className="kpis">
              {KPIS.map((k) => (
                <button key={k.key} type="button" className={`kpi${status === k.key ? ' active' : ''}`} style={{ textAlign: 'left', cursor: 'pointer', font: 'inherit' }}
                  onClick={() => set({ bb: status === k.key ? null : k.key, page: null })}>
                  <span className="k-label">{k.label}</span>
                  <span className="k-value" style={k.key === 'competing' && d.summary.competing ? { color: 'var(--err)' } : undefined}>{int(d.summary[k.key as keyof Page['summary']] as number ?? 0)}</span>
                  <span className="k-note">{k.note}</span>
                </button>
              ))}
            </div>
          )}

          {d && d.rivals.length > 0 && (
            <section className="panel" style={{ padding: 14, display: 'grid', gap: 8 }}>
              <b>Quem mais ganha de você</b>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {d.rivals.map((r) => (
                  <div key={r.sellerId} className="btn sm" style={{ cursor: 'default', gap: 8, background: String(r.sellerId) === params.get('seller') ? 'var(--surface-2)' : undefined }}>
                    <button type="button" className="link" style={{ background: 'none', border: 0, padding: 0, font: 'inherit', cursor: 'pointer', color: 'inherit' }}
                      onClick={() => set({ seller: String(r.sellerId) === params.get('seller') ? null : String(r.sellerId), page: null })}
                      title="Ver os anúncios em que ele ganha de você">
                      <b>{r.nickname ?? `Vendedor ${r.sellerId}`}</b> · {int(r.products)} produto{r.products === 1 ? '' : 's'}{r.avgGap != null && r.avgGap > 0 ? ` · ${pct(r.avgGap)} mais barato` : ''}
                    </button>
                    {r.followed ? <span className="small muted">acompanhando</span>
                      : <button type="button" className="btn sm quiet" style={{ height: 24 }} onClick={() => onFollow(r.sellerId)} title="Acompanhar como concorrente"><Eye size={13} /> Acompanhar</button>}
                  </div>
                ))}
              </div>
            </section>
          )}

          <div className="toolbar">
            <label className="search">
              <Search size={16} />
              <span className="sr-only">Buscar</span>
              <input className="input" type="search" placeholder="Buscar por nome, SKU, EAN ou MLB" value={search} onChange={(e) => setSearch(e.target.value)} />
            </label>
            <select className="select" aria-label="Situação" value={status} onChange={(e) => set({ bb: e.target.value === 'all' ? null : e.target.value, page: null })}>
              <option value="all">Todas as situações</option>
              <option value="competing">Perdendo</option>
              <option value="by_price">Perdendo por preço</option>
              <option value="sharing_first_place">Dividindo o 1º lugar</option>
              <option value="listed">Só listado</option>
              <option value="winning">Ganhando</option>
              <option value="changed">Mudou nos últimos 7 dias</option>
              <option value="no_margin">Ganhar fica abaixo do custo</option>
            </select>
            <MarginRange params={params} set={set} />
            <ListingCategorySelect channel="mercadolivre" params={params} set={set} />
            <AttrFilterButton params={params} set={set} />
            {params.get('seller') && <button type="button" className="btn" onClick={() => set({ seller: null, page: null })}>Ganhador: {sellerName ?? params.get('seller')} ✕</button>}
            <button className="btn" type="button" disabled={exporting || !d?.total} onClick={exportAll}><Download size={15} /> {exporting ? 'Exportando…' : 'Exportar'}</button>
            {d?.summary.checked_at && <span className="small muted">Lido {dateTime(d.summary.checked_at)}</span>}
          </div>
          {apply.error && <ErrorBox error={apply.error} />}
          {admin && d && d.total > 0 && status !== 'winning' && (
            <div className="bulkbar" role="group" aria-label="Aplicar o buy box em todos" style={{ flexWrap: 'wrap' }}>
              <span>Aplicar o buy box nos <b>{int(d.total)}</b> anúncios deste filtro:</span>
              <select className="select" aria-label="Preço a aplicar" value={mode} onChange={(e) => setMode(e.target.value as 'to_win' | 'cover')}>
                <option value="to_win">Preço indicado pelo ML para ganhar</option>
                <option value="cover">Cobrir o ganhador (1 centavo abaixo)</option>
              </select>
              <label className="small" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                <input type="checkbox" className="check" checked={allowLoss} onChange={(e) => setAllowLoss(e.target.checked)} />
                Incluir os que ficariam com prejuízo
              </label>
              <button type="button" className="btn sm primary" disabled={applyAll.isPending}
                onClick={() => {
                  const msg = `Aplicar o buy box em até ${int(d.total)} anúncios? O preço vira preço na mão do produto e vai ao Mercado Livre na próxima rodada.`
                    + (allowLoss ? ' Inclui os que ficariam com prejuízo.' : ' Os que ficariam com prejuízo ficam de fora.');
                  if (window.confirm(msg)) applyAll.mutate();
                }}><Zap size={14} /> {applyAll.isPending ? 'Aplicando…' : 'Aplicar em todos'}</button>
              {applyAll.data && (
                <span className="small">
                  <b>{int(applyAll.data.applied)}</b> produto{applyAll.data.applied === 1 ? '' : 's'} com preço novo
                  {applyAll.data.skipped.loss > 0 && <> · {int(applyAll.data.skipped.loss)} de fora por prejuízo</>}
                  {applyAll.data.skipped.noPrice > 0 && <> · {int(applyAll.data.skipped.noPrice)} sem preço menor para aplicar</>}
                  {applyAll.data.skipped.noProduct > 0 && <> · {int(applyAll.data.skipped.noProduct)} sem produto vinculado</>}
                  {!!applyAll.data.skipped.outOfRange && <> · {int(applyAll.data.skipped.outOfRange)} fora da faixa de margem</>}
                </span>
              )}
            </div>
          )}
          {applyAll.error && <ErrorBox error={applyAll.error} />}

          <div className="panel flush">
            {!data.isPending && rows.length === 0 ? <div className="empty"><b>Nada por aqui</b>Mude a busca ou o filtro.</div> : (
              <>
                <div className="grid-wrap" style={{ opacity: data.isPlaceholderData ? 0.55 : 1 }}>
                  <table className="grid" style={{ width: '100%', minWidth: 1280 }}>
                    <colgroup>{COLS.map(([, w], i) => <col key={i} style={w ? { width: w } : undefined} />)}</colgroup>
                    <thead><tr>{COLS.map(([l, , c], i) => <th key={i} className={c}>{l}</th>)}</tr></thead>
                    <tbody>
                      {data.isPending
                        ? Array.from({ length: 8 }, (_, i) => <tr key={i}>{COLS.map((_, j) => <td key={j}><div className="skeleton" /></td>)}</tr>)
                        : rows.map((r) => (
                            <tr key={r.id} style={{ cursor: 'default' }}>
                              <td><Thumb src={r.thumbnail} alt="" /></td>
                              <td>
                                <div className="cell-2">
                                  <span>{r.title}</span>
                                  <span className="sub">
                                    {r.permalink ? <a className="ext" href={r.permalink} target="_blank" rel="noreferrer">{r.itemId} <ExternalLink size={11} /></a> : r.itemId}
                                    {r.sku && <> · {r.sku}</>}{r.full && <> · <Tag tone="ok">Full</Tag></>}
                                  </span>
                                </div>
                              </td>
                              <td>
                                <Tag tone={STATUS[r.status]?.tone ?? 'off'}>{STATUS[r.status]?.label ?? r.status}</Tag>
                                {r.prevStatus && r.statusChangedAt && <div className="small muted" title={dateTime(r.statusChangedAt)}>era {STATUS[r.prevStatus]?.label.toLowerCase() ?? r.prevStatus}</div>}
                                {r.status === 'sharing_first_place' && r.sharing != null && <div className="small muted">com {r.sharing} vendedor{r.sharing === 1 ? '' : 'es'}</div>}
                              </td>
                              <td className="num">{r.cost != null ? brl(r.cost) : <span className="small muted">—</span>}</td>
                              <td className="num">{brl(r.price)}</td>
                              <td>
                                {r.status === 'winning' ? <span className="small muted">você</span> : (
                                  <div className="cell-2">
                                    <span>
                                      <b>{brl(r.winnerPrice)}</b>
                                      {r.winnerGapPct != null && Math.abs(r.winnerGapPct) > 0.0001 && (
                                        <span className="small" style={{ color: r.winnerGapPct < 0 ? 'var(--err)' : 'var(--ok)' }}> {r.winnerGapPct > 0 ? '+' : ''}{pct(r.winnerGapPct)}</span>
                                      )}
                                    </span>
                                    <span className="sub">
                                      {r.winner ? (r.winner.permalink ? <a className="ext" href={r.winner.permalink} target="_blank" rel="noreferrer">{r.winner.nickname ?? r.winner.sellerId}</a> : r.winner.nickname ?? r.winner.sellerId)
                                        : 'vendedor não informado'}
                                      {r.winner?.full && ' · Full'}{r.winnerBoosts.includes('Sem juros') && ' · sem juros'}
                                    </span>
                                  </div>
                                )}
                              </td>
                              <td className="num">
                                <div title={r.calc ? `${brl(r.price)} − tarifa do canal ${brl(r.calc.fees)} − custo ${brl(r.calc.cost)} = ${brl(r.calc.profit)}` : 'Margem com o seu preço atual'}
                                  style={{ cursor: r.calc ? 'help' : undefined }}>{pctOf(r.marginNow)}</div>
                                {r.coverPrice != null && r.marginCover != null && (
                                  <div className="small" style={{ color: r.marginCover < 0 ? 'var(--err)' : 'var(--muted)' }} title={`Margem cobrindo o ganhador a ${brl(r.coverPrice)}`}>cobrindo: {pctOf(r.marginCover)}</div>
                                )}
                              </td>
                              <td>
                                {r.coverPrice != null ? (
                                  <div style={{ display: 'grid', gap: 2, justifyItems: 'start' }}>
                                    <button type="button" className={`btn sm${r.marginCover != null && r.marginCover < 0 ? ' danger' : ''}`} disabled={apply.isPending}
                                      title="Grava esse preço no produto e envia ao Mercado Livre na próxima rodada"
                                      onClick={() => {
                                        if (r.marginCover != null && r.marginCover < 0 && !window.confirm(`Cobrir a ${brl(r.coverPrice)} deixa a margem em ${pctOf(r.marginCover)} (prejuízo). Aplicar mesmo assim?`)) return;
                                        apply.mutate({ id: r.id, price: r.coverPrice! });
                                      }}>Cobrir {brl(r.coverPrice)}</button>
                                    {r.priceNotEnough && <span className="small muted" title="O Mercado Livre indica que só o preço não faz você ganhar: veja o que falta">preço pode não bastar</span>}
                                  </div>
                                ) : r.status !== 'winning' && r.priceToWin != null && r.price != null && r.priceToWin < r.price - 0.009 ? (
                                  <button type="button" className="btn sm" disabled={apply.isPending} onClick={() => apply.mutate({ id: r.id })}
                                    title="Preço indicado pelo Mercado Livre para ganhar">Usar {brl(r.priceToWin)}</button>
                                ) : <span className="small muted">—</span>}
                              </td>
                              <td>
                                {r.reasons.length > 0 ? <span className="small" style={{ color: 'var(--warn)', whiteSpace: 'normal' }}>{r.reasons.join('; ')}</span>
                                  : r.missing.length > 0 ? <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>{r.missing.map((m) => <Tag key={m} tone="warn">{m}</Tag>)}</div>
                                  : <span className="small muted">—</span>}
                              </td>
                            </tr>
                          ))}
                    </tbody>
                  </table>
                </div>
                <div className="cards">
                  {rows.map((r) => (
                    <div key={r.id} className="card-row">
                      <Thumb src={r.thumbnail} alt="" />
                      <div style={{ minWidth: 0 }}>
                        <div className="t">{r.title}</div>
                        <div className="m"><Tag tone={STATUS[r.status]?.tone ?? 'off'}>{STATUS[r.status]?.label ?? r.status}</Tag>{r.winner?.nickname && <> · {r.winner.nickname}</>}</div>
                      </div>
                      <div className="r">{brl(r.price)}{r.status !== 'winning' && r.winnerPrice != null && <div className="small">ganhador: {brl(r.winnerPrice)}</div>}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
            {d && <Pager total={d.total} page={page} pageSize={d.pageSize} onPage={(p) => set({ page: p > 1 ? String(p) : null })} />}
          </div>
        </>
      )}
    </div>
  );
}

/** Faixa de margem (%): margem no preço indicado pelo ML, cobrindo o ganhador ou no preço atual. Vale também no aplicar todos. */
function MarginRange({ params, set }: { params: URLSearchParams; set: (p: Record<string, string | null>) => void }) {
  const [a, setA] = useState(params.get('marginMin') ?? '');
  const [b, setB] = useState(params.get('marginMax') ?? '');
  const num = (v: string) => (v.trim() === '' ? null : String(Number(v.replace(',', '.'))));
  const apply = () => set({ marginMin: num(a), marginMax: num(b), page: null });
  const key = (e: React.KeyboardEvent) => { if (e.key === 'Enter') apply(); };
  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }} className="small">
      <select className="select" aria-label="Margem de qual preço" value={params.get('marginOf') ?? 'to_win'} onChange={(e) => set({ marginOf: e.target.value === 'to_win' ? null : e.target.value, page: null })}>
        <option value="to_win">Margem no preço indicado</option>
        <option value="cover">Margem cobrindo o ganhador</option>
        <option value="now">Margem atual</option>
      </select>
      de <input className="input" style={{ width: 70 }} inputMode="decimal" placeholder="%" aria-label="Margem mínima (%)" value={a} onChange={(e) => setA(e.target.value)} onBlur={apply} onKeyDown={key} />
      até <input className="input" style={{ width: 70 }} inputMode="decimal" placeholder="%" aria-label="Margem máxima (%)" value={b} onChange={(e) => setB(e.target.value)} onBlur={apply} onKeyDown={key} />
    </span>
  );
}
